/**
 * Namaz vakitleri — Diyanet İşleri Başkanlığı'nın yayımladığı takvim.
 *
 * ezanvakti.emushaf.net ilçe bazında bir aylık takvimi tek çağrıda
 * veriyor, anahtar istemiyor. Koordinattan hesaplayan yöntemler
 * Diyanet'in takviminden birkaç dakika şaşabiliyor; namaz vaktinde o
 * birkaç dakika mesele olduğu için resmî takvim tercih edildi.
 *
 * Teheccüd takvimde yok: imsaktan iki saat öncesi olarak veriliyor.
 */
import { useQuery } from '@tanstack/react-query';
import type { Event, ScheduleItem } from './types';

export interface Vakit {
  ad: string;
  saat: string; // "HH:MM"
}

export interface GunVakti {
  tarih: string; // YYYY-MM-DD
  vakitler: Vakit[];
}

/** Teheccüd: imsaktan iki saat önce. */
export const TEHECCUD_ONCE_DK = 120;

/** Namaz satırı vaktin 5 dakika öncesinde başlar, 30 dakika sürer. */
export const HAZIRLIK_DK = 5;
export const NAMAZ_SURE_DK = 30;

export const bugunTarih = () => new Date().toLocaleDateString('sv-SE');

/** "14.08.2026" → "2026-08-14" */
function tarihCevir(t: string): string {
  const [g, a, y] = t.split('.');
  return `${y}-${a}-${g}`;
}

/** "04:33" → dakika */
export const dakika = (s: string) => {
  const [a, b] = s.split(':').map(Number);
  return a * 60 + b;
};

export const saatMetni = (d: number) => {
  const g = ((d % 1440) + 1440) % 1440;
  return `${String(Math.floor(g / 60)).padStart(2, '0')}:${String(g % 60).padStart(2, '0')}`;
};

export async function vakitleriAl(ilceId: string): Promise<GunVakti[]> {
  const c = await fetch(`https://ezanvakti.emushaf.net/vakitler/${ilceId}`);
  if (!c.ok) throw new Error('Vakitler alınamadı');
  const ham = (await c.json()) as Record<string, string>[];

  return ham.map((g) => {
    const imsak = dakika(g.Imsak);
    return {
      tarih: tarihCevir(g.MiladiTarihKisa),
      vakitler: [
        { ad: 'Teheccüd', saat: saatMetni(imsak - TEHECCUD_ONCE_DK) },
        { ad: 'İmsak', saat: g.Imsak },
        { ad: 'Güneş', saat: g.Gunes },
        { ad: 'Öğle', saat: g.Ogle },
        { ad: 'İkindi', saat: g.Ikindi },
        { ad: 'Akşam', saat: g.Aksam },
        { ad: 'Yatsı', saat: g.Yatsi },
      ],
    };
  });
}

/** Programın namaz vakitlerini çeken ortak sorgu. */
export function useNamazVakitleri(event?: Event) {
  const ilce = event?.prayerDistrictId;
  return useQuery({
    queryKey: ['namaz', ilce],
    queryFn: () => vakitleriAl(ilce!),
    enabled: !!ilce,
    /* Aylık takvim; gün içinde değişmiyor. */
    staleTime: 12 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/** Şu andan sonraki ilk vakit; gün bittiyse yarının ilk vakti. */
export function sonrakiVakit(
  gunler: GunVakti[],
): { vakit: Vakit; yarin: boolean; kalanDk: number } | null {
  const b = bugunTarih();
  const simdi = new Date();
  const su = simdi.getHours() * 60 + simdi.getMinutes();

  const bugunku = gunler.find((g) => g.tarih === b);
  if (bugunku) {
    const v = bugunku.vakitler.find((x) => dakika(x.saat) > su);
    if (v) return { vakit: v, yarin: false, kalanDk: dakika(v.saat) - su };
  }

  const i = gunler.findIndex((g) => g.tarih === b);
  const yarinki = i >= 0 ? gunler[i + 1] : undefined;
  if (yarinki?.vakitler.length) {
    const v = yarinki.vakitler[0];
    return { vakit: v, yarin: true, kalanDk: 1440 - su + dakika(v.saat) };
  }
  return null;
}

/**
 * Akışa işlenen namazlar. Güneş bir namaz vakti değil (sabahın çıkışı),
 * o yüzden burada yok; vakit listesinde görünmeye devam ediyor.
 *
 * `anahtar` elle yazılmış satırı tanımak için: akışta zaten "Öğle
 * Namazı" varsa üstüne bir de otomatik satır konmuyor.
 */
const NAMAZLAR = [
  { kaynak: 'Teheccüd', baslik: 'Teheccüd', anahtar: ['teheccüd', 'teheccud'] },
  { kaynak: 'İmsak', baslik: 'Sabah Namazı', anahtar: ['sabah', 'imsak', 'i̇msak'] },
  { kaynak: 'Öğle', baslik: 'Öğle Namazı', anahtar: ['öğle', 'ogle'] },
  { kaynak: 'İkindi', baslik: 'İkindi Namazı', anahtar: ['ikindi', 'i̇kindi'] },
  { kaynak: 'Akşam', baslik: 'Akşam Namazı', anahtar: ['akşam', 'aksam'] },
  { kaynak: 'Yatsı', baslik: 'Yatsı Namazı', anahtar: ['yatsı', 'yatsi'] },
];

/** Otomatik satırların kimliği bu önekle başlar. */
export const NAMAZ_ONEK = 'namaz:';
export const otomatikNamazMi = (item: ScheduleItem) => item.id.startsWith(NAMAZ_ONEK);

/**
 * Verilen günler için namaz satırlarını üretir; akışta aynı namaz elle
 * girilmişse o gün için atlanır.
 *
 * Program akışı hiç kurulmamış olsa da vakitler görünsün diye satırlar
 * veritabanına yazılmıyor, gösterimde akışa katılıyor.
 */
export function namazSatirlari(
  gunler: GunVakti[] | undefined,
  gunlerListesi: string[],
  eventId: string,
  mevcut: ScheduleItem[],
): ScheduleItem[] {
  if (!gunler?.length) return [];
  const satirlar: ScheduleItem[] = [];

  for (const tarih of gunlerListesi) {
    const gun = gunler.find((g) => g.tarih === tarih);
    if (!gun) continue;

    const oGunun = mevcut.filter((s) => s.dayDate === tarih).map((s) => s.title.toLocaleLowerCase('tr'));

    NAMAZLAR.forEach((n, i) => {
      if (oGunun.some((t) => n.anahtar.some((a) => t.includes(a)))) return;
      const v = gun.vakitler.find((x) => x.ad === n.kaynak);
      if (!v) return;

      const bas = dakika(v.saat) - HAZIRLIK_DK;
      satirlar.push({
        id: `${NAMAZ_ONEK}${tarih}:${n.kaynak}`,
        eventId,
        dayDate: tarih,
        startTime: saatMetni(bas),
        endTime: saatMetni(bas + NAMAZ_SURE_DK),
        title: n.baslik,
        description: `Vakit ${v.saat} · Diyanet takvimi`,
        activityType: 'prayer',
        sortOrder: i,
      });
    });
  }

  return satirlar;
}

/** Program tarih aralığındaki bütün günler. */
export function programGunleri(event?: Event): string[] {
  if (!event?.startDate) return [];
  const bas = new Date(`${event.startDate}T00:00:00`);
  const bit = new Date(`${(event.endDate ?? event.startDate)}T00:00:00`);
  const gunler: string[] = [];
  /* Uzun aralıklarda takvim bir aylık geldiği için üst sınır konuyor. */
  for (let g = bas; g <= bit && gunler.length < 40; g.setDate(g.getDate() + 1)) {
    gunler.push(g.toLocaleDateString('sv-SE'));
  }
  return gunler;
}
